import React, { useEffect, useRef } from "react";
import { sanitizeRichText } from "./RichText";
import { Bold, Italic, Underline, List, ListOrdered } from "lucide-react";

/**
 * Lightweight rich-text editor for the question TEXT block.
 * - contentEditable + document.execCommand for B/I/U/lists
 * - Saves sanitized HTML via onChange
 */
export default function RichTextEditor({ value, onChange, testId, placeholder }) {
  const ref = useRef(null);

  // Sync incoming value into the DOM ONCE / when it doesn't match.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value || "";
    if (el.innerHTML !== incoming) el.innerHTML = incoming;
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    // trigger onChange after DOM mutates
    setTimeout(() => onChange(sanitizeRichText(ref.current?.innerHTML || "")), 0);
  };

  return (
    <div className="rounded-md border border-[#262626] bg-[#0A0A0A]">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-[#262626] bg-[#171717]">
        <TB onClick={() => exec("bold")} testId={`${testId}-bold`} title="Kalın (Ctrl+B)"><Bold size={14}/></TB>
        <TB onClick={() => exec("italic")} testId={`${testId}-italic`} title="İtalik (Ctrl+I)"><Italic size={14}/></TB>
        <TB onClick={() => exec("underline")} testId={`${testId}-underline`} title="Altı çizili (Ctrl+U)"><Underline size={14}/></TB>
        <span className="mx-1 h-4 w-px bg-[#262626]" />
        <TB onClick={() => exec("insertUnorderedList")} testId={`${testId}-ul`} title="Madde işaretli liste"><List size={14}/></TB>
        <TB onClick={() => exec("insertOrderedList")} testId={`${testId}-ol`} title="Numaralı liste"><ListOrdered size={14}/></TB>
      </div>
      <div
        ref={ref}
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(sanitizeRichText(e.currentTarget.innerHTML))}
        onBlur={(e) => onChange(sanitizeRichText(e.currentTarget.innerHTML))}
        onPaste={(e) => {
          e.preventDefault();
          const txt = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, txt);
        }}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
            const k = e.key.toLowerCase();
            if (k === "b") { e.preventDefault(); exec("bold"); }
            else if (k === "i") { e.preventDefault(); exec("italic"); }
            else if (k === "u") { e.preventDefault(); exec("underline"); }
          }
        }}
        className="min-h-[80px] px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#EA580C] rounded-b-md prose-rt"
        data-placeholder={placeholder || "Metin yaz…"}
      />
    </div>
  );
}

function TB({ children, onClick, testId, title }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      data-testid={testId} title={title}
      className="p-1.5 rounded hover:bg-[#0A0A0A] text-slate-300">
      {children}
    </button>
  );
}
